import { UISref } from "@uirouter/react";
import { FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "../../../components/button";
import { Icon } from "../../../components/icons";
import { Loader } from "../../../components/loader";
import { Table } from "../../../components/table";
import { TableHeaders } from "../../../components/table/Head";
import { Widget } from "../../../components/widget";
import { Pageable } from "../../../services/pageable";
import { AppStore } from "../../../store";
import { useFetchable } from "../../../store/common/fetchable";
import { VerificationActions } from "../../../store/verification/actions";
import { VerificationDTO } from "../../../store/verification/models/dto/due-diligence";
import { Verification } from "../../../store/verification/reducer";
import { colours } from "../../../styles/colours";
import { MerchantDueDiligenceRoutes } from "../routes";
import { NoDueDiligenceFormConfigs } from "./empty";

type SingleDueDiligenceForm = VerificationDTO.Responses.DueDiligence.Config.Get.T;
type fieldNames = keyof SingleDueDiligenceForm;

const useHeaders: () => TableHeaders<fieldNames> = () =>
  useMemo(
    () => [
      {
        name: "createdAt",
        title: "Date Created",
      },
      {
        name: "emailAddress",
        title: "E-mail Address",
      },
      {
        name: "expiresAt",
        title: "Expiration Date",
      },
      {
        name: "url",
        title: "URL",
      },
      {
        name: "id",
        title: "",
        centered: true,
      },
    ],
    []
  );

export const AllDueDiligence = () => {
  const data = useFetchable(
    Verification.allDueDiligenceConfig,
    useMemo<
      Pageable.T<keyof VerificationDTO.Responses.DueDiligence.Config.Get.T>
    >(
      () => ({
        pagination: {
          size: 20,
          page: 0,
        },
      }),
      []
    )
  );

  return (
    <Loader data={[data]}>
      {([fulfilled]) => <PresentationalAllDueDiligence data={fulfilled} />}
    </Loader>
  );
};

const PresentationalAllDueDiligence: FunctionComponent<{
  data: VerificationDTO.Responses.DueDiligence.Config.GetAll.T;
}> = ({ data }) => {
  const dispatch = useDispatch();

  const pageableState = useSelector<
    AppStore,
    AppStore["verification"]["allDueDiligenceConfig"]["pagination"]
  >((store) => store.verification.allDueDiligenceConfig.pagination);

  const onPaginationChange = (x: Pageable.T<fieldNames>) => {
    dispatch(VerificationActions.setAllDueDiligenceConfigPagination(x));
  };

  const headers = useHeaders();

  return (
    <Widget title="All Due Diligence Forms">
      {data.pageable.totalElements < 1 ? (
        <NoDueDiligenceFormConfigs />
      ) : (
        <Table
          data={data.content}
          head={headers}
          renderFields={{
            expiresAt: ({ expiresAt }) => (
              <>{expiresAt?.local().format("yyy-MM-DD HH:mm")}</>
            ),
            createdAt: ({ createdAt }) => (
              <>{createdAt?.local().format("yyy-MM-DD HH:mm")}</>
            ),
            url: ({ url }) => (
              <a target="_blank" href={url}>
                {url}
              </a>
            ),
            id: ({ id }) => (
              <UISref
                to={MerchantDueDiligenceRoutes.names.single}
                params={{ id: id }}
              >
                <a>
                  <PrimaryButton variant="contained">
                    <Icon
                      type="export"
                      rotateAmount={270}
                      colour1={colours.white}
                    />
                  </PrimaryButton>
                </a>
              </UISref>
            ),
          }}
          unsortable={["id", "url"]}
          pagination={data.pageable}
          onPaginationChange={onPaginationChange}
          pageableState={pageableState}
        />
      )}
    </Widget>
  );
};
